import { useMutation, useQueryClient } from "@tanstack/react-query";

import * as mineApi from "../api";
import { computeContentHash } from "../content-hash";
import { mineQueryKeys } from "../query-options";
import type {
  AddItemsData,
  BatchAddItemTagsData,
  BatchRemoveItemTagsData,
  CreateImageItemData,
  CreateFolderData,
  CreateTagData,
  CreateTextItemData,
  PurgeDeletedItemsData,
  RestoreDeletedItemsData,
  UpdateFolderData,
  UpdateItemsData,
  UpdateTagData,
} from "../types";

export async function uploadToSignedUrl(
  signedUrl: string,
  file: Blob,
  contentType: string,
  onProgress?: (progress: number) => void,
) {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open("PUT", signedUrl);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) {
        return;
      }

      onProgress?.(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
        return;
      }

      reject(new Error("文件上传失败"));
    };
    xhr.onerror = () => reject(new Error("文件上传失败"));
    xhr.onabort = () => reject(new Error("文件上传已取消"));
    xhr.send(file);
  });
}

export function useAddItemMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: AddItemsData) => mineApi.addItems(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });
    },
  });
}

export function useCreateTextItemMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateTextItemData) =>
      mineApi.addItems({
        folder_id: payload.folder_id,
        format: "text",
        tags: payload.tags,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });
    },
  });
}

export function useCreateImageItemMutation() {
  return useMutation({
    mutationFn: async (payload: CreateImageItemData) => {
      const hashKey = await computeContentHash(payload.file);
      const uploadTask = await mineApi.createUploadTasks({
        file_name: payload.file.name,
        format: "item",
        mime_type: payload.file.type || "application/octet-stream",
        size: String(payload.file.size),
      });

      await uploadToSignedUrl(
        uploadTask.upload_url,
        payload.file,
        payload.file.type || "application/octet-stream",
        payload.onUploadProgress,
      );

      return mineApi.addItems({
        upload_task_id: uploadTask.id,
        hash_key: hashKey,
        folder_id: payload.folder_id,
        format: "image",
        title: payload.title?.trim() || undefined,
        note: payload.note?.trim() || undefined,
        tags: payload.tags,
        mime_type:
          payload.analysis?.mime_type ||
          payload.file.type ||
          "application/octet-stream",
        size: String(payload.file.size),
        image: {
          width: payload.analysis?.width,
          height: payload.analysis?.height,
          orientation: payload.analysis?.orientation,
          visual: payload.analysis?.visual,
        }
      });
    },
  });
}

export function useUpdateItemMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateItemsData) => mineApi.updateItems(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });
    },
  });
}

export function useMoveItemsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { items: { id: string; folder_id?: string }[]; folderId: string | null }) => {
      const targetFolderId = payload.folderId || "0";
      const itemsToMove = payload.items.filter((item) => (item.folder_id || "0") !== targetFolderId);

      if (itemsToMove.length > 0) {
        await mineApi.moveItems({
          ids: itemsToMove.map((item) => item.id),
          folder_id: targetFolderId,
        });
      }

      return {
        movedCount: itemsToMove.length,
        itemIds: itemsToMove.map((item) => item.id),
        folderId: payload.folderId,
      };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });
    },
  });
}

export function useDeleteItemMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string | string[]) => mineApi.deleteItems(ids),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });
    },
  });
}

export function useRestoreDeletedItemsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RestoreDeletedItemsData) =>
      mineApi.restoreDeletedItems(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.deletedItems });
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });
    },
  });
}

export function usePurgeDeletedItemsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload?: PurgeDeletedItemsData) =>
      mineApi.purgeDeletedItems(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.deletedItems });
    },
  });
}

export function useCreateFolderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateFolderData) => mineApi.createFolder(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.folders });
    },
  });
}

export function useBatchAddItemTagsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BatchAddItemTagsData) => mineApi.batchAddItemTags(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });
    },
  });
}

export function useBatchRemoveItemTagsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BatchRemoveItemTagsData) => mineApi.batchRemoveItemTags(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });
    },
  });
}

export function useUpdateFolderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateFolderData) => mineApi.updateFolder(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.folders });
    },
  });
}

export function useDeleteFolderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => mineApi.deleteFolder(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.folders });
    },
  });
}

export function useCreateTagMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateTagData) => mineApi.createTag(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.tags });
    },
  });
}

export function useUpdateTagMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateTagData) => mineApi.updateTag(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.tags });
    },
  });
}

export function useDeleteTagMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => mineApi.deleteTag(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });
      void queryClient.invalidateQueries({ queryKey: mineQueryKeys.tags });
    },
  });
}
